import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { platformService } from '@/services/api/platform.service';
import type { PlatformUser } from '@/types/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PlatformLayout } from '@/components/platform/PlatformLayout';

/**
 * Super-admin impersonation picker. Lists tenant users across all orgs
 * (search by email) and starts impersonation via the auth context.
 */
export default function PlatformUsersPage() {
  const [, setLocation] = useLocation();
  const { startImpersonation } = useAuth();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const search = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const { users } = await platformService.listUsers(q);
      setUsers(users);
    } catch (err: any) {
      // No/invalid platform session → back to platform login.
      if (err?.response?.status === 401) setLocation('/platform/login');
    } finally {
      setLoading(false);
    }
  }, [setLocation]);

  useEffect(() => {
    search('');
  }, [search]);

  const handleImpersonate = async (id: number) => {
    setBusyId(id);
    try {
      await startImpersonation(id);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to impersonate');
      setBusyId(null);
    }
  };

  return (
    <PlatformLayout title="Impersonate a user">

      <form
        onSubmit={(e) => { e.preventDefault(); search(query.trim()); }}
        className="mb-4 flex gap-2"
      >
        <Input
          placeholder="Search by email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button type="submit" variant="primary" disabled={loading}>Search</Button>
      </form>

      <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-ink-subtle">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Organization</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-line">
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">{u.role}</td>
                <td className="px-3 py-2">{u.organizationName ?? u.organizationId}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="outline"
                    onClick={() => handleImpersonate(u.id)}
                    disabled={busyId === u.id}
                  >
                    {busyId === u.id ? 'Starting…' : 'Impersonate'}
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-ink-subtle">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PlatformLayout>
  );
}
